import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './RegisterForm.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const RegisterForm = ({ onClose, onSuccess, initialRole = 'resident' }) => {
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    confirmPassword: '',
    role: initialRole,
    full_name: '',
    email: '',
    phone: '',
    // Resident fields
    iin: '',
    address: '',
    district: '',
    // Service worker fields
    service_type: '',
    department: '',
    position: '',
    service_id: '',
    supervisor_email: ''
  });

  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const districts = [
    'Центральный район',
    'Северный район', 
    'Южный район',
    'Восточный район',
    'Западный район'
  ];

  // Load services when component mounts
  useEffect(() => {
    loadServices();
  }, []);

  const loadServices = async () => {
    try {
      const response = await axios.get(`${API}/services`);
      setServices(response.data);
    } catch (err) {
      console.error('Error loading services:', err);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Basic validation
    if (!formData.username.trim()) newErrors.username = 'Логин обязателен';
    if (!formData.password) newErrors.password = 'Пароль обязателен';
    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Пароли не совпадают';
    }
    if (!formData.full_name.trim()) newErrors.full_name = 'ФИО обязательно';
    if (!formData.email.trim()) newErrors.email = 'Email обязателен';
    if (!formData.phone.trim()) newErrors.phone = 'Телефон обязателен';

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (formData.email && !emailRegex.test(formData.email)) {
      newErrors.email = 'Неверный формат email';
    }

    // Role-specific validation
    if (formData.role === 'resident') {
      if (!formData.iin.trim()) newErrors.iin = 'ИИН обязателен';
      if (!formData.address.trim()) newErrors.address = 'Адрес обязателен';
      
      // IIN format validation (12 digits)
      if (formData.iin && (formData.iin.length !== 12 || !/^\d+$/.test(formData.iin))) {
        newErrors.iin = 'ИИН должен состоять из 12 цифр';
      }
    } else if (formData.role === 'service') {
      if (!formData.service_type) newErrors.service_type = 'Тип службы обязателен';
      if (!formData.department.trim()) newErrors.department = 'Отдел обязателен';
      if (!formData.position.trim()) newErrors.position = 'Должность обязательна';
    }

    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setLoading(true);
    setErrors({});

    try {
      const response = await axios.post(`${API}/auth/register`, formData);
      onSuccess({
        message: response.data.message,
        user_id: response.data.user_id
      });
    } catch (err) {
      console.error('Registration error:', err);
      const errorMessage = err.response?.data?.detail || 'Ошибка регистрации';
      setErrors({ submit: errorMessage });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="register-overlay">
      <div className="register-form">
        <div className="register-header">
          <h2>Регистрация в системе</h2>
          <button className="close-btn" onClick={onClose}>✕</button>
        </div>

        <form onSubmit={handleSubmit} className="register-form-content">
          {/* Role Selection */}
          <div className="form-section">
            <h3>Выберите роль</h3>
            <div className="role-selection">
              <label className={`role-option ${formData.role === 'resident' ? 'selected' : ''}`}>
                <input
                  type="radio"
                  name="role"
                  value="resident"
                  checked={formData.role === 'resident'}
                  onChange={handleChange}
                />
                <span>👤 Житель города Алтай</span>
              </label>
              <label className={`role-option ${formData.role === 'service' ? 'selected' : ''}`}>
                <input
                  type="radio"
                  name="role"
                  value="service"
                  checked={formData.role === 'service'}
                  onChange={handleChange}
                />
                <span>🔧 Сотрудник службы</span>
              </label>
            </div>
          </div>

          {/* Basic Information */}
          <div className="form-section">
            <h3>Основная информация</h3>
            <div className="form-grid">
              <div className="form-group">
                <label>Логин *</label>
                <input
                  type="text"
                  name="username"
                  value={formData.username}
                  onChange={handleChange}
                  className={errors.username ? 'error' : ''}
                  placeholder="Введите логин"
                />
                {errors.username && <span className="error-text">{errors.username}</span>}
              </div>

              <div className="form-group">
                <label>Пароль *</label>
                <input
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  className={errors.password ? 'error' : ''}
                  placeholder="Введите пароль"
                />
                {errors.password && <span className="error-text">{errors.password}</span>}
              </div>

              <div className="form-group">
                <label>Подтвердите пароль *</label>
                <input
                  type="password"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  className={errors.confirmPassword ? 'error' : ''}
                  placeholder="Повторите пароль"
                />
                {errors.confirmPassword && <span className="error-text">{errors.confirmPassword}</span>}
              </div>

              <div className="form-group full-width">
                <label>ФИО *</label>
                <input
                  type="text"
                  name="full_name"
                  value={formData.full_name}
                  onChange={handleChange}
                  className={errors.full_name ? 'error' : ''}
                  placeholder="Введите полное имя"
                />
                {errors.full_name && <span className="error-text">{errors.full_name}</span>}
              </div>

              <div className="form-group">
                <label>Email *</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className={errors.email ? 'error' : ''}
                  placeholder="example@email.com"
                />
                {errors.email && <span className="error-text">{errors.email}</span>}
              </div>

              <div className="form-group">
                <label>Телефон *</label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className={errors.phone ? 'error' : ''}
                  placeholder="+7 (XXX) XXX-XX-XX"
                />
                {errors.phone && <span className="error-text">{errors.phone}</span>}
              </div>
            </div>
          </div>

          {/* Resident-specific fields */}
          {formData.role === 'resident' && (
            <div className="form-section">
              <h3>Информация жителя</h3>
              <div className="form-grid">
                <div className="form-group">
                  <label>ИИН *</label>
                  <input
                    type="text"
                    name="iin"
                    value={formData.iin}
                    onChange={handleChange}
                    className={errors.iin ? 'error' : ''}
                    placeholder="123456789012"
                    maxLength="12"
                  />
                  {errors.iin && <span className="error-text">{errors.iin}</span>}
                </div>

                <div className="form-group">
                  <label>Район</label>
                  <select
                    name="district"
                    value={formData.district}
                    onChange={handleChange}
                  >
                    <option value="">Выберите район</option>
                    {districts.map(district => (
                      <option key={district} value={district}>{district}</option>
                    ))}
                  </select>
                </div>

                <div className="form-group full-width">
                  <label>Адрес проживания в г. Алтай *</label>
                  <input
                    type="text"
                    name="address"
                    value={formData.address}
                    onChange={handleChange}
                    className={errors.address ? 'error' : ''}
                    placeholder="ул. Примерная, д. 1, кв. 1"
                  />
                  {errors.address && <span className="error-text">{errors.address}</span>}
                </div>
              </div>
            </div>
          )}

          {/* Service worker-specific fields */}
          {formData.role === 'service' && (
            <div className="form-section">
              <h3>Информация сотрудника</h3>
              <div className="form-grid">
                <div className="form-group">
                  <label>Тип службы *</label>
                  <select
                    name="service_type"
                    value={formData.service_type}
                    onChange={handleChange}
                    className={errors.service_type ? 'error' : ''}
                  >
                    <option value="">Выберите службу</option>
                    {services.map(service => (
                      <option key={service.id} value={service.id}>
                        {service.name_ru}
                      </option>
                    ))}
                  </select>
                  {errors.service_type && <span className="error-text">{errors.service_type}</span>}
                </div>

                <div className="form-group">
                  <label>Отдел *</label>
                  <input
                    type="text"
                    name="department"
                    value={formData.department}
                    onChange={handleChange}
                    className={errors.department ? 'error' : ''}
                    placeholder="Название отдела"
                  />
                  {errors.department && <span className="error-text">{errors.department}</span>}
                </div>

                <div className="form-group">
                  <label>Должность *</label>
                  <input
                    type="text"
                    name="position"
                    value={formData.position}
                    onChange={handleChange}
                    className={errors.position ? 'error' : ''}
                    placeholder="Ваша должность"
                  />
                  {errors.position && <span className="error-text">{errors.position}</span>}
                </div>

                <div className="form-group">
                  <label>Служебный номер</label>
                  <input
                    type="text"
                    name="service_id"
                    value={formData.service_id}
                    onChange={handleChange}
                    placeholder="Служебный номер (если есть)"
                  />
                </div>

                <div className="form-group full-width">
                  <label>Email руководителя</label>
                  <input
                    type="email"
                    name="supervisor_email"
                    value={formData.supervisor_email}
                    onChange={handleChange}
                    placeholder="supervisor@service.kz (для подтверждения)"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Submit */}
          <div className="form-actions">
            {errors.submit && <div className="error-message">{errors.submit}</div>}
            
            <div className="verification-notice">
              <p><strong>Внимание:</strong> После регистрации ваш аккаунт будет отправлен на модерацию. 
              Для активации потребуется загрузить документы и дождаться подтверждения администратором.</p>
            </div>

            <button 
              type="submit" 
              className="submit-btn" 
              disabled={loading}
            >
              {loading ? 'Регистрация...' : 'Зарегистрироваться'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RegisterForm;